import { Breadcrumb, Button, Form, Input, Select } from "antd";
import styles from "./search.module.scss";
import "../../auxiliary/lookup/lookup.scss";
import { _adr } from "../../../address";
import { HomeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Content } from "antd/es/layout/layout";
import { validateInput } from "../../../tools/tools";
import ReactInputMask from "react-input-mask";
import { useEffect, useState } from "react";
import { useAPIPublic } from "../../../tools/api";
import { useNavigate } from "react-router-dom";
import OTP from "../../auxiliary/otp-modal/otp";
import { CheckTokenWithOTP, setTokenOTP } from "../../../tools/services";

const NATIONAL_CODE = 0;
// same id getFieldDocument maps to "Fida"
const FIDA_CODE = 7;
const digits = (v) => (v ?? "").toString().replace(/\D/g, "");

const Search360 = () => {
	const [t] = useTranslation("global");
	const [frmService] = Form.useForm();
	const nav = useNavigate();

	const { queryCertType } = useAPIPublic();

	const [isSearch, setIsSearch] = useState({
		certType: [],
		certTypeValue: null,
	});
	// subscriber waiting for OTP confirmation; the modal is mounted only while this is set
	const [otpTarget, setOtpTarget] = useState(null);

	useEffect(() => {
		const callServCertType = async () => {
			let response = await queryCertType();
			if (response) {
				setIsSearch((s) => ({ ...s, certType: response }));
			}
		};
		callServCertType();
	}, []);

	// straight to OTP: SendOTPCodeAsync with checkmsisdnvcertno=true makes the backend verify that the mobile
	// belongs to this cert number and refuse to send the code (with its own error message) if it does not
	const handleSubmit = (values) => {
		// sessionid is fixed per attempt: the modal sends and confirms the code with the same id
		setOtpTarget({ msisdn: digits(values?.smsmobile), certno: values.certno, certtype: values.certtype, sessionid: Date.now() });
	};

	// OTP modal result: null = cancelled / wrong code / send failed, otherwise the confirmed OTP token
	const handleOtp = async (token) => {
		const target = otpTarget;
		setOtpTarget(null);
		if (!token) return;
		await setTokenOTP({ expireUTCTime: token.expireUTCTime, token: token.token });
		if (await CheckTokenWithOTP()) {
			const { sessionid, ...state } = target;
			nav(_adr.Root + _adr.Services360.Index + _adr.Services360.List, { state });
		}
	};

	const isNational = isSearch.certTypeValue !== null && Number(isSearch.certTypeValue) === NATIONAL_CODE;
	const isFida = isSearch.certTypeValue !== null && Number(isSearch.certTypeValue) === FIDA_CODE;
	// the other fields stay locked until a cert type is chosen (it decides how the cert number is validated)
	const locked = isSearch.certTypeValue === null;

	return (
		<>
			<div className={`container-main ${styles.container_360}`}>
				<div className="g-hold-title-of-page">
					<Breadcrumb className="breadcrumbs" items={[{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> }, { title: t("services360.title") }]} />
					<div className="title-page">
						<h3>{t("services360.title")}</h3>
						<p></p>
					</div>
				</div>
				<div className="g-layout-content">
					<Content className={`g-layout-main lookup-page ${styles.service360_container}`}>
						<div className="lookup-grid">
							<div className="lookup-main">
								<div className="lookup-mark" aria-hidden="true">
									<i className="icon-service360"></i>
								</div>
								<h4>جستجوی مشترک</h4>
								<p className="lookup-hint">شماره موبایل و مدرک شناسایی مشترک را وارد کنید.</p>

								<Form id="frmService" name="frmService" layout="vertical" form={frmService} onFinish={handleSubmit} className={styles.form}>
									<Form.Item className="input-normal" name="certtype" rules={[validateInput({ t: t, validate: true })]} label="نوع مدرک شناسایی">
										<Select
											placeholder="انتخاب کنید"
											onChange={(value) => {
												setIsSearch((s) => ({ ...s, certTypeValue: value }));
												if (frmService.getFieldValue("certno")) frmService.validateFields(["certno"]);
											}}>
											{isSearch.certType.map((item, key) => (
												<Select.Option key={key} value={item.value}>
													{item.name}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
									<div className={styles.pair}>
										<Form.Item
											className="input-normal"
											name="certno"
											label={isNational ? "کد ملی" : "شماره مدرک شناسایی"}
											rules={
												isNational
													? [
															{
																validator: (_, value) =>
																	/^\d{10}$/.test(value ?? "") ? Promise.resolve() : Promise.reject("کد ملی خود را صحیح وارد نمایید"),
															},
														]
													: isFida
														? // the Fida code has no agreed format (digits, letters, any length), so it is only required
															[validateInput({ t: t, validate: true })]
														: [
																validateInput({
																	t: t,
																	validate: true,
																	type: "english",
																}),
															]
											}>
											<Input
												dir="ltr"
												autoComplete="off"
												inputMode={isFida ? "text" : "numeric"}
												maxLength={isNational ? 10 : isFida ? undefined : 11}
												disabled={locked}
												placeholder={locked ? "ابتدا نوع مدرک را انتخاب کنید" : undefined}
											/>
										</Form.Item>
										<Form.Item
											className="input-normal"
											name="smsmobile"
											label="شماره موبایل"
											// any operator's number is accepted, it only has to be 11 digits
											rules={[
												{
													validator: (_, value) =>
														digits(value).length === 11 ? Promise.resolve() : Promise.reject("شماره موبایل خود را صحیح وارد نمایید"),
												},
											]}>
											<div>
												<ReactInputMask autoComplete="off" inputMode="numeric" className="msisdn-mask" dir="ltr" mask="0\999 999 99 99" disabled={locked} />
											</div>
										</Form.Item>
									</div>
									<Button className="lookup-btn" htmlType="submit" block disabled={!!otpTarget}>
										{otpTarget ? t("form.inProgress") : "ارسال کد تایید"}
									</Button>
								</Form>
							</div>

							<aside className="lookup-aside" aria-label="مراحل جستجو">
								<h5>مراحل</h5>
								<ol className="lookup-steps">
									<li>نوع مدرک، موبایل و شماره مدرک مشترک را وارد کنید.</li>
									<li>کد تایید به موبایل مشترک پیامک می‌شود؛ اگر موبایل متعلق به همان شخص نباشد، کدی ارسال نمی‌شود.</li>
									<li>با وارد کردن کد، اطلاعات مشترک و شماره‌های آپتل او نمایش داده می‌شود.</li>
								</ol>
								<p>کد تایید ۲ دقیقه اعتبار دارد؛ پس از آن می‌توانید دوباره درخواست کنید.</p>
							</aside>
						</div>
					</Content>
				</div>
			</div>

			{otpTarget && (
				<OTP
					isOpen
					// ponytail: no 360-specific plan id exists; the dealer plan is used for the OTP SMS
					planid={process.env.REACT_APP_PLANID_DEALER}
					msisdn={otpTarget.msisdn.slice(1)}
					certno={otpTarget.certno}
					certtype={otpTarget.certtype}
					checkmsisdnvcertno={true}
					// the modal's countdown is (expiry - 1 minute): 3 minutes => 120 seconds before "resend" appears
					expiretimebyminute={3}
					sessionid={otpTarget.sessionid}
					callback={handleOtp}
				/>
			)}
		</>
	);
};

export default Search360;
